import { useEffect, useMemo, useRef, useState } from "react";
import { useAction, useMutation as useConvexMutation } from "convex/react";
import * as Sentry from "@sentry/react";
import Icon from "./Icon";
import { GlassCard, Btn } from "./UI";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useDialogA11y } from "@/lib/useDialogA11y";
import { api } from "../../../../convex/_generated/api";
import type { RecipeIngredient } from "@/lib/store";

/**
 * IngredientEditor
 *
 * Bottom sheet on RecipeDetail that lets a sister adjust the quantity,
 * change the unit, or swap the entire ingredient via USDA search.
 *
 * Props:
 *   open       - controls visibility
 *   onClose    - dismiss handler
 *   recipeId   - Convex recipe domain id
 *   index      - position of the ingredient in recipe.ingredients
 *   current    - the current ingredient object
 *
 * Flow:
 *   1. Sister taps an ingredient → this sheet opens with current values.
 *   2. She adjusts qty (+/- buttons or direct input), changes unit
 *      (dropdown of common American units), or types in the search box
 *      to find a different ingredient.
 *   3. Tap "Save" → mutation patches the ingredient at that index, then
 *      action re-runs USDA aggregation, recipe row's macros refresh
 *      via Convex's reactive query.
 *
 * UX intent: feel like editing a single row in-place, not a heavyweight
 * form. Defaults to qty/unit edit on open; search expands when she
 * focuses the search field.
 */

const COMMON_UNITS = [
  "cup",
  "tbsp",
  "tsp",
  "oz",
  "lb",
  "g",
  "kg",
  "ml",
  "fl oz",
  "clove",
  "slice",
  "piece",
  "",
];

interface SearchResult {
  fdcId: number;
  description: string;
  kcalPer100g: number;
  proteinPer100g: number;
}

/**
 * Search lifecycle. An empty result list means nothing on its own — "we asked
 * and the database has no such food" and "the search never answered" look
 * identical from a bare array, and telling a sister her ingredient doesn't
 * exist when the service is down is a lie. Every empty list is labelled.
 */
type SearchStatus = "idle" | "pending" | "ok" | "failed";

export default function IngredientEditor({
  open,
  onClose,
  recipeId,
  index,
  current,
}: {
  open: boolean;
  onClose: () => void;
  recipeId: string;
  index: number;
  current: RecipeIngredient;
}) {
  const [name, setName] = useState(current.item);
  const [qty, setQty] = useState<string>(String(current.qty || 1));
  const [unit, setUnit] = useState(current.unit || "");
  // fdcId is set when the user picks a USDA search result. Cleared when
  // they type a custom name. Persisted on save so populateNutrition can
  // use the exact match.
  const [fdcId, setFdcId] = useState<number | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchStatus, setSearchStatus] = useState<SearchStatus>("idle");
  const [saving, setSaving] = useState(false);
  // Rendered in the sheet when the save fails. The sheet stays open and holds
  // her edits so nothing looks like it landed when it didn't.
  const [saveError, setSaveError] = useState<string>("");

  const updateIngredient = useConvexMutation(api.recipesDb.updateIngredient);
  const populateNutrition = useAction(api.recipesDb.populateNutrition);
  const searchIngredients = useAction(api.foodDb.searchIngredients);

  // Reset local state when the sheet opens for a different ingredient.
  // Includes index + recipeId so swapping target while open also resets,
  // and clears all transient flags (saving, searching, fdcId) too.
  useEffect(() => {
    if (open) {
      setName(current.item);
      setQty(String(current.qty || 1));
      setUnit(current.unit || "");
      setFdcId(undefined);
      setSearchQuery("");
      setSearchResults([]);
      setSearchStatus("idle");
      setSaving(false);
      setSaveError("");
    }
  }, [open, recipeId, index, current.item, current.qty, current.unit]);

  // Debounced search: fire 350ms after typing stops. Uses a sequence
  // counter so a slow earlier response doesn't overwrite a faster later
  // response (race-safe). Also tracks "mounted" so we don't setState on
  // an unmounted component.
  const searchSeqRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!open || searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearchStatus("idle");
      return;
    }
    // Go "pending" the moment she types, not when the debounce fires, so an
    // empty list is never briefly labelled "no matches" before we have asked.
    setSearchStatus("pending");
    const handle = setTimeout(async () => {
      const mySeq = ++searchSeqRef.current;
      try {
        const results = await searchIngredients({ query: searchQuery.trim(), limit: 8 });
        // Only commit results if no later search has been issued + we're still mounted.
        if (mountedRef.current && mySeq === searchSeqRef.current) {
          setSearchResults(results);
          setSearchStatus("ok");
        }
      } catch (err) {
        console.error("[IngredientEditor] ingredient search failed", err);
        Sentry.captureException(err, { tags: { subsystem: "ingredient-editor" } });
        if (mountedRef.current && mySeq === searchSeqRef.current) {
          setSearchResults([]);
          setSearchStatus("failed");
        }
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [searchQuery, open, searchIngredients]);

  const adjustQty = (delta: number) => {
    const n = Number(qty);
    if (!Number.isFinite(n)) return;
    // Sensible step: 0.25 below 4, 1 above
    const step = n < 4 ? 0.25 : 1;
    const next = Math.max(0, Math.round((n + delta * step) * 100) / 100);
    setQty(String(next));
  };

  const pickSearchResult = (result: SearchResult) => {
    setName(result.description);
    setFdcId(result.fdcId);
    setSearchQuery("");
    setSearchResults([]);
  };

  /**
   * Re-aggregate macros after the ingredient itself is already saved.
   *
   * A failure here is NOT a failed save, so it must not block or undo
   * anything. But the recipe would then render stale or zeroed macros with no
   * explanation, and unexplained zeros in a nutrition app read as "this food
   * has no calories". So she is told plainly, and handed a retry.
   */
  const refreshNutrition: (targetRecipeId: string) => void = (targetRecipeId) => {
    void populateNutrition({ recipeId: targetRecipeId }).catch((err: unknown) => {
      console.error("[IngredientEditor] nutrition refresh failed", err);
      Sentry.captureException(err, { tags: { subsystem: "ingredient-editor" } });
      toast({
        variant: "destructive",
        title: "Nutrition didn't refresh",
        description:
          "Your ingredient change was saved. We just couldn't add the macros back up, so the numbers on this recipe aren't right yet.",
        action: (
          <ToastAction
            altText="Try adding up the nutrition again"
            onClick={() => refreshNutrition(targetRecipeId)}
          >
            Retry
          </ToastAction>
        ),
      });
    });
  };

  const onSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const cleanQty = Math.max(0, Number(qty) || 0);
      await updateIngredient({
        recipeId,
        index,
        ingredient: {
          qty: cleanQty,
          unit: unit.trim(),
          item: name.trim() || current.item,
          ...(fdcId !== undefined ? { fdcId } : {}),
        },
      });
      // Re-aggregate macros from the updated ingredient list. Deliberately not
      // awaited: the ingredient is saved either way.
      refreshNutrition(recipeId);
      onClose();
    } catch (err) {
      console.error("[IngredientEditor] updateIngredient mutation failed", err);
      Sentry.captureException(err, { tags: { subsystem: "ingredient-editor" } });
      // Sheet stays open with her edits intact — nothing here may look saved.
      setSaveError(
        "We couldn't save that change, so your ingredient is still as it was. Check your connection and tap Save again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const isDirty = useMemo(() => {
    return (
      name.trim() !== current.item ||
      String(current.qty || 1) !== qty ||
      (current.unit || "") !== unit
    );
  }, [name, qty, unit, current.item, current.qty, current.unit]);

  const titleId = "ingredient-editor-title";
  const { containerRef } = useDialogA11y({ open, onClose, titleId });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)", zIndex: 70 }}
      onClick={onClose}
      data-testid="ingredient-editor-overlay"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--mnf-cream)",
          borderRadius: "24px 24px 0 0",
          padding: 22,
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Edit ingredient</p>
            <h2
              id={titleId}
              className="mnf-serif"
              style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15 }}
            >
              Adjust or swap
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="button-close-ingredient-editor"
            className="hover-elevate"
            style={{
              width: 36, height: 36, borderRadius: 999,
              background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)",
              border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Current ingredient */}
        <GlassCard padding={16}>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", marginBottom: 6 }}>
            Ingredient
          </p>
          <input
            data-testid="input-ingredient-name"
            aria-label="Ingredient name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mnf-body w-full"
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid rgba(30,74,53,0.18)",
              background: "rgba(255,255,255,0.65)",
              color: "var(--mnf-fd)",
              fontSize: 14,
              outline: "none",
            }}
          />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", marginBottom: 6 }}>Quantity</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => adjustQty(-1)}
                  data-testid="button-qty-down"
                  aria-label="Decrease quantity"
                  className="hover-elevate"
                  style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: "rgba(30,74,53,0.08)", color: "var(--mnf-fd)",
                    border: "none", cursor: "pointer", fontSize: 18,
                  }}
                >−</button>
                <input
                  data-testid="input-qty"
                  aria-label="Quantity"
                  type="number"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  step="0.25"
                  min="0"
                  className="mnf-body text-center"
                  style={{
                    flex: 1, padding: "8px 6px", borderRadius: 10,
                    border: "1px solid rgba(30,74,53,0.18)",
                    background: "rgba(255,255,255,0.65)",
                    color: "var(--mnf-fd)", fontSize: 14, outline: "none",
                    fontFamily: "var(--mnf-mono, monospace)",
                  }}
                />
                <button
                  onClick={() => adjustQty(1)}
                  data-testid="button-qty-up"
                  aria-label="Increase quantity"
                  className="hover-elevate"
                  style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: "rgba(30,74,53,0.08)", color: "var(--mnf-fd)",
                    border: "none", cursor: "pointer", fontSize: 18,
                  }}
                >+</button>
              </div>
            </div>
            <div>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", marginBottom: 6 }}>Unit</p>
              <select
                data-testid="select-unit"
                aria-label="Unit of measurement"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="mnf-body w-full"
                style={{
                  padding: "10px 12px", borderRadius: 10,
                  border: "1px solid rgba(30,74,53,0.18)",
                  background: "rgba(255,255,255,0.65)",
                  color: "var(--mnf-fd)", fontSize: 14, outline: "none",
                }}
              >
                {COMMON_UNITS.map((u) => (
                  <option key={u || "none"} value={u}>
                    {u || "(none)"}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </GlassCard>

        {/* Swap via search */}
        <div className="mt-3">
          <GlassCard padding={16}>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", marginBottom: 6 }}>
              Swap ingredient
            </p>
            <input
              data-testid="input-search"
              aria-label="Search the USDA food database to swap ingredient"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search USDA database (e.g. coconut aminos)"
              className="mnf-body w-full"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(30,74,53,0.18)",
                background: "rgba(255,255,255,0.65)",
                color: "var(--mnf-fd)",
                fontSize: 13,
                outline: "none",
              }}
            />
            {searchStatus === "pending" && (
              <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 8 }}>
                Searching…
              </p>
            )}
            {searchStatus === "failed" && (
              <p
                role="alert"
                data-testid="search-error"
                className="mnf-body"
                style={{ fontSize: 11.5, color: "var(--mnf-t)", marginTop: 8, lineHeight: 1.5 }}
              >
                Ingredient search isn't answering right now, so we can't look anything up
                yet. This isn't about your ingredient — type the name in above and save, or
                try searching again in a moment.
              </p>
            )}
            {searchStatus === "ok" && searchResults.length === 0 && (
              <p
                data-testid="search-empty"
                className="mnf-body"
                style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 8, lineHeight: 1.5 }}
              >
                No foods matched that one. Try a simpler word and we'll look again.
              </p>
            )}
            {searchResults.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {searchResults.map((r) => (
                  <button
                    key={r.fdcId}
                    data-testid={`button-swap-${r.fdcId}`}
                    onClick={() => pickSearchResult(r)}
                    className="hover-elevate w-full text-left"
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      background: "rgba(127,119,221,0.06)",
                      border: "1px solid rgba(127,119,221,0.18)",
                      cursor: "pointer",
                    }}
                  >
                    <p
                      className="mnf-body"
                      style={{ fontSize: 12.5, fontWeight: 600, color: "var(--mnf-fd)" }}
                    >
                      {r.description}
                    </p>
                    <p
                      className="mnf-body"
                      style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 2 }}
                    >
                      {Math.round(r.kcalPer100g)} kcal · {r.proteinPer100g.toFixed(1)}g protein per 100g
                    </p>
                  </button>
                ))}
              </div>
            )}
          </GlassCard>
        </div>

        {saveError && (
          <p
            role="alert"
            data-testid="ingredient-save-error"
            className="mnf-body"
            style={{ fontSize: 12, color: "var(--mnf-t)", lineHeight: 1.5, marginTop: 14 }}
          >
            {saveError}
          </p>
        )}

        <div className="mt-4">
          <Btn
            data-testid="button-save-ingredient"
            variant="primary"
            block
            size="lg"
            onClick={onSave}
            disabled={saving || !isDirty}
          >
            <Icon name="check" size={18} /> {saving ? "Saving…" : "Save"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
